import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit3, FileText } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", body: "", category: "General" };
const CATEGORIES = ["General", "Reminder", "Cancellation", "Payment", "Enrollment", "Event", "Win-Back"];

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/templates"); setItems(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) { toast.error("Name and body required"); return; }
    try {
      if (editId) await api.put(`/templates/${editId}`, form);
      else await api.post("/templates", form);
      toast.success(editId ? "Template updated" : "Template created");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this template?")) return;
    try { await api.delete(`/templates/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (t) => {
    setForm({ name: t.name, body: t.body, category: t.category || "General" });
    setEditId(t.id);
    setOpen(true);
  };

  const grouped = items.reduce((acc, t) => {
    const c = t.category || "General";
    (acc[c] = acc[c] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="templates-page">
      <div className="max-w-5xl mx-auto p-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <FileText className="w-7 h-7 text-[#D9232E]" /> Message Templates
            </h1>
            <p className="text-sm text-slate-500 mt-1">{items.length} templates ready to drop into any message.</p>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="new-template-button">
                <Plus className="w-4 h-4 mr-2" /> New Template
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="template-dialog">
              <DialogHeader><DialogTitle>{editId ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Costume Fitting Reminder" data-testid="template-name" /></div>
                <div>
                  <Label>Category</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {CATEGORIES.map((c) => (
                      <Button key={c} type="button" size="sm" variant={form.category === c ? "default" : "outline"} onClick={() => setForm({ ...form, category: c })} className={form.category === c ? "bg-[#D9232E] hover:bg-[#B91C25]" : ""} data-testid={`template-cat-${c}`}>
                        {c}
                      </Button>
                    ))}
                  </div>
                </div>
                <div><Label>Message body</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} placeholder="Type the message..." data-testid="template-body" /></div>
                <div className="text-xs text-slate-400">{form.body.length} characters</div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="save-template-button">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{cat} <span className="text-slate-400 font-normal">({list.length})</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map((t) => (
                  <div key={t.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow" data-testid={`template-row-${t.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-bold text-slate-900 text-[15px]">{t.name}</div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(t)} title="Edit" data-testid={`edit-template-${t.id}`}>
                          <Edit3 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => del(t.id)} title="Delete" data-testid={`delete-template-${t.id}`}>
                          <Trash2 className="w-4 h-4 text-slate-500 hover:text-[#D9232E]" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed">{t.body}</div>
                    <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] text-slate-400">{t.body.length} chars</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
